import { Redirect } from '~~/shared-components/redirect/settings'

// Registers this app's project-specific redirect routes on top of the engine's
// defaults (shared-components/redirect/settings.ts). Runs at app init, before
// any navigation, so the `link` / `redirect` middleware see the extended
// registry.
//
// Auth actions that already work via the engine defaults + this app's pages —
// no registration needed here:
//   eshop:login            -> route `login`          (app/pages/login.vue)
//   eshop:logout           -> route `logout`         (app/pages/logout.vue, logoutPage meta)
//   eshop:register         -> route `register`       (app/pages/register.vue -> membership-order)
//   eshop:account:recovery -> route `reset-password` (app/pages/reset-password.vue,
//                                                      reads { token, email, new })
//
// The engine resolves an action via getDefaultRedirectRoutes()[action] FIRST,
// then getExtendedRedirectRoute(action). So defaults cannot be overridden here —
// extendRedirectRoutes only adds NEW project keys.
export default defineNuxtPlugin(() => {
  const redirect = Redirect.getInstance()

  // Auth-gated routes: resolve to the target only when logged in, otherwise send
  // the user to login and remember where they were headed. extendPernamentRoutes
  // does not auth-gate (it maps key -> plain page name), so gating lives in a
  // customAction.
  redirect.extendRedirectRoutes({
    // /link?action=eshop:account
    'eshop:account': {
      customAction: (query, locale) => {
        const { $localePath, $auth } = useNuxtApp()
        const auth = useAuth()

        if (auth.value?.user) {
          return navigateTo($localePath({ name: 'account', query }, locale))
        }

        $auth.setBackLink($localePath({ name: 'account' }, locale))
        return navigateTo($localePath({ name: 'login', query }, locale))
      },
    },

    // /link?action=eshop:answer:detail&id=<answerId>&type=text|video
    // Sent by the "your question was answered" e-mail. Where it lands depends on the
    // answer type: a video answer has its own detail page, a text answer is inlined into
    // the question accordion on /dotazy.
    //
    // The accordion is keyed by QUESTION id and the link only carries the ANSWER id, so
    // the text branch hands the answer id to the page as `?answer=` and lets it find the
    // owning question once the list has loaded. /dotazy also re-checks that id against the
    // loaded video group, so a link with a missing or wrong `type` still ends up on the
    // right page.
    'eshop:answer:detail': {
      customAction: (query, locale) => {
        const { $localePath, $auth } = useNuxtApp()
        const auth = useAuth()

        const first = (v: unknown) => (Array.isArray(v) ? v[0] : v)
        const id = first(query.id) as string | undefined
        const type = String(first(query.type) ?? '')

        const target =
          id && type === 'video'
            ? { name: 'dotazy-id', params: { id } }
            : { name: 'dotazy', query: id ? { answer: id } : undefined }

        // Both /dotazy pages are `public: true` and rely on the content guard, which sends
        // a signed-out visitor to the landing page — a poor ending for an e-mail deep link.
        // Gate here instead so the target survives the login round-trip, same as
        // `eshop:account` above.
        if (!auth.value?.user) {
          $auth.setBackLink($localePath(target, locale))
          return navigateTo($localePath({ name: 'login' }, locale))
        }

        return navigateTo($localePath(target, locale))
      },
    },

    // /link?action=eshop:order:confirmation&order=<orderGuid>
    // Where the payment gateway sends the buyer once the payment is settled — lands on the
    // membership confirmation page instead of the generic order detail (`eshop:order:detail`).
    // The order guid rides along as `?order=`: it is the only thing identifying the order
    // on the confirmation page, which fetches it and bounces anything that doesn't resolve.
    // Nothing is carried in cookies — a return from the gateway is a cross-site navigation,
    // where they wouldn't be sent anyway.
    'eshop:order:confirmation': {
      pageName: 'membership-confirmation',
      query: (query) => {
        const order = Array.isArray(query.order) ? query.order[0] : query.order
        return order ? { order: String(order) } : {}
      },
    },

    // /link?action=eshop:subscription:cancel&guid=<subscriptionGuid>
    // Anonymous recurring-subscription cancellation (e-mail deep link). The page reads the
    // guid from its route param and POSTs the cancel action itself — no auth gating.
    'eshop:subscription:cancel': {
      pageName: 'subscription-cancel-guid',
      params: (query) => {
        const guid = Array.isArray(query.guid) ? query.guid[0] : query.guid
        return { guid: String(guid ?? '') }
      },
    },
  })

  // Permanent routes (content links / 404 fallback resolution): map a project
  // key to an existing route name. Not auth-gated — use a redirect route
  // customAction (above) when gating is required.
  redirect.extendPernamentRoutes({
    'eshop:account': 'account',
  })
})
